<template>
  <div class="invite-overlay" @click.self="$emit('close')" @keydown="handleKeydown">
    <section
      ref="dialog"
      class="invite-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-title"
    >
      <button ref="closeButton" type="button" class="invite-close" aria-label="Close invite" @click="$emit('close')">
        &times;
      </button>

      <div class="invite-content">
        <div class="invite-heading">
          <p class="invite-eyebrow">Bring everyone in</p>
          <h2 id="invite-title">Invite singers</h2>
          <p class="invite-room-code">Room {{ roomId }}</p>
        </div>

        <div class="invite-qr-frame">
          <VueQrcode
            :value="joinUrl"
            :options="{
              width: 240,
              margin: 3,
              color: { dark: '#000000', light: '#ffffff' }
            }"
          />
        </div>

        <p class="invite-instructions">Scan the QR code or share the room link.</p>

        <div class="invite-actions">
          <button type="button" class="invite-copy" @click="$emit('copy')">
            {{ copySuccess ? 'Copied' : 'Copy link' }}
          </button>
          <button v-if="canShare" type="button" class="invite-share" @click="$emit('share')">
            Share
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import VueQrcode from '@chenfengyuan/vue-qrcode'

export default {
  name: 'RoomInviteModal',
  components: { VueQrcode },
  props: {
    roomId: {
      type: String,
      required: true
    },
    joinUrl: {
      type: String,
      required: true
    },
    copySuccess: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'copy', 'share'],
  setup(_, { emit }) {
    const dialog = ref(null)
    const closeButton = ref(null)
    const canShare = computed(() => typeof navigator.share === 'function')

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        emit('close')
        return
      }

      if (event.key !== 'Tab' || !dialog.value) return

      const focusable = Array.from(dialog.value.querySelectorAll('button:not([disabled])'))
      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    onMounted(() => closeButton.value?.focus())

    return { canShare, closeButton, dialog, handleKeydown }
  }
}
</script>

<style scoped>
.invite-overlay {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: grid;
  place-items: center;
  padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px);
}

.invite-dialog {
  position: relative;
  width: min(100%, 25rem);
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  border: 1px solid rgba(255, 165, 0, 0.2);
  border-radius: 1.25rem;
  background: linear-gradient(160deg, #19120d, #090807 70%);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.65);
  color: white;
}

.invite-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: clamp(1.25rem, 5vw, 2rem);
}

.invite-heading {
  text-align: center;
}

.invite-eyebrow,
.invite-room-code,
.invite-instructions {
  margin: 0;
}

.invite-eyebrow {
  color: #ffa500;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.invite-heading h2 {
  margin: 0.3rem 0 0.4rem;
  font-size: clamp(1.5rem, 7vw, 2rem);
}

.invite-room-code {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.invite-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1;
  width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.7rem;
  cursor: pointer;
}

.invite-qr-frame {
  max-width: 100%;
  padding: clamp(0.65rem, 3vw, 0.9rem);
  overflow: hidden;
  border-radius: 1rem;
  background: white;
}

.invite-qr-frame :deep(canvas) {
  display: block;
  width: min(60vw, 240px) !important;
  height: auto !important;
  max-width: 100%;
}

.invite-instructions {
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.86rem;
  text-align: center;
}

.invite-actions {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
  gap: 0.65rem;
}

.invite-copy,
.invite-share {
  min-height: 48px;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.invite-copy {
  border: 0;
  background: #ffa500;
  color: #1a0c00;
}

.invite-share {
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: white;
}

@media (max-height: 620px) {
  .invite-content {
    gap: 0.65rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .invite-qr-frame :deep(canvas) {
    width: min(48vw, 190px) !important;
  }
}
</style>
